import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from '@calm-web/design-system';

import { MAIN_CONTENT_ID } from '@/components/accessibility/SkipToMain';
import Header from '@/components/header/Header';
import Image from '@/components/image/NextImage';
import { useAnalytics } from '@/hooks/analytics';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import genericMessages from '@/messages/messages';
import { getPrices } from '@/server/initState/getPrices';
import { setTheme } from '@/store/theme/actions';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { calmLogger } from '@/utils/calmLogger';

import messages from './messages';
import { BgVideo, Column, Container, Content, Heading, LeBronLogo, Wrapper } from './styles';
import { LeBronLayoutProps } from './types';

const LeBronLayout = ({ isDiscountFlow = false }: LeBronLayoutProps) => {
	const dispatch = useDispatch();
	const { formatMessage } = useIntl();
	const routerPush = useRouterPush();
	const { logEvent } = useAnalytics();

	const [percentDiscount, setPercentDiscount] = useState<number | null>();
	const [isFetching, setIsFetching] = useState(true);

	const onClick = async () => {
		logEvent({
			eventName: 'Landing Page : CTA : Clicked',
			eventProps: { section: 'hero' },
		});

		await routerPush(isDiscountFlow ? '/subscribe' : '/signup-flow');
	};

	const fetchDiscount = async () => {
		try {
			const prices = await getPrices({ coupon: '40percent_welcome_offer' }, getUserData());
			setPercentDiscount(prices?.coupons?.yearly?.percent_off);
		} catch (err) {
			setPercentDiscount(null);
		} finally {
			setIsFetching(false);
		}
	};

	useEffect(
		() => {
			dispatch(
				setTheme({
					header: 'dark',
					footer: 'light',
				}),
			);

			if (isDiscountFlow) {
				fetchDiscount().catch(error => calmLogger.error('Error in LeBronLayout fetchDiscount', {}, error));
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	return (
		<Wrapper id={MAIN_CONTENT_ID}>
			<Header hideMenu hideProfile />
			<Container>
				<Column>
					<BgVideo>
						<video
							loop
							muted
							playsInline
							autoPlay
							disablePictureInPicture
							controlsList="nodownload noplaybackrate"
							poster="/_n/images/lebron/lebron-video-placeholder.webp"
						>
							<source src="/_n/videos/lebron/lebron-sky-bg.mp4" type="video/mp4" />
						</video>
					</BgVideo>
					<Content>
						<Heading>{formatMessage(messages.heading)}</Heading>
						<LeBronLogo />
						<Button
							fullWidth
							backgroundColor="white"
							textColor="black"
							maxWidth="270px"
							onPress={onClick}
							loaderColor="gray6"
							isLoading={isDiscountFlow && isFetching}
						>
							{isDiscountFlow && percentDiscount
								? formatMessage(genericMessages.getPercentOff, {
										percent: percentDiscount,
									})
								: formatMessage(genericMessages.getStarted)}
						</Button>
					</Content>
				</Column>
				<Column>
					<Image
						fill
						style={{ objectPosition: 'center', objectFit: 'cover' }}
						alt="LeBron sitting in space"
						src="/_n/images/lebron/lebron-bg.webp"
					/>
				</Column>
			</Container>
		</Wrapper>
	);
};

export default LeBronLayout;
